import { capture } from '@parcel/analytics/browser';
import type { SpecBillingSummary } from '@parcel/types';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { ChevronDown, MoreHorizontal, Search, Trash2, UserPlus } from 'lucide-react';
import * as React from 'react';
import { apiErrorCode } from '../../lib/api.js';
import { useEntitlements } from '../../lib/entitlements.js';
import { getSpecBilling } from '../../lib/spec-billing.js';
import { isSpecSurfaceVisible } from '../../lib/spec-config.js';
import { useSpecLaunchState } from '../../lib/spec-launch.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import {
  getWorkspace,
  getWorkspaceSecurityDomains,
  getWorkspaceSlugFromPath,
  inviteWorkspaceMembers,
  listWorkspaceMembers,
  resendWorkspaceInvitation,
  revokeWorkspaceInvitation,
} from '../../lib/workspaces.js';
import { AuthenticatedImage } from '../auth/authenticated-image.js';
import { ManageSeatsDialog } from '../billing/manage-seats-dialog.js';
import { SeatBanner, seatBannerState } from '../billing/seat-banner.js';
import {
  type MemberFilter,
  type MemberRole,
  type WorkspaceMember,
  filterMembers,
  getActiveMemberCount,
  getInviteCount,
  getInviteDomainWarnings,
  getInviteErrorMessages,
  getMemberInitials,
  getMemberStatusLabel,
  mapWorkspaceMembersResponse,
  mergeInviteResponsePendingMembers,
  removeInvitationMember,
} from './members-settings.js';
import { SettingsPageHeader, SettingsPanel, SettingsShell } from './settings-shell.js';
import { TableSkeletonRows } from './settings-skeletons.js';
import { SpecSeatTierSelect } from './spec-seat-tier.js';
import { seatPhrase } from './usage-settings.js';

const memberFilters: MemberFilter[] = ['All', 'Admin', 'Member'];

function MemberStatusBadge({ member }: { member: WorkspaceMember }) {
  const statusLabel = getMemberStatusLabel(member);

  if (statusLabel === 'Pending') return <Badge variant="amber">Pending</Badge>;
  if (statusLabel === 'Expired') return <Badge variant="red">Expired</Badge>;
  if (statusLabel === 'Retry needed') return <Badge variant="red">Retry needed</Badge>;

  return <Badge variant="secondary">{statusLabel}</Badge>;
}

/**
 * Headless: fetches the Spec billing summary the `Tier` column needs and
 * reports it up via `onSummaryChange` rather than rendering the column
 * itself. The header count, the extra `TableHead`, and each row's
 * `SpecSeatTierSelect` all live in `MembersSettingsPage`'s own table -- not
 * inside this component -- so they read the ONE summary this fetches instead
 * of each independently subscribing to the same query.
 */
function SpecSeatColumnReader({
  workspaceId,
  onSummaryChange,
  onLoadFailed,
}: {
  workspaceId: string;
  onSummaryChange: (summary: SpecBillingSummary | null) => void;
  /**
   * Reports whether the read FAILED, separately from whether it produced a
   * summary. A failure and a read still in flight both leave `summary` null,
   * and they mean opposite things: one is "wait", the other is "the Tier
   * column is gone and nobody said why".
   */
  onLoadFailed: (failed: boolean) => void;
}) {
  // Same query key as SpecBillingCard/SpecUsageSection, so a workspace open on
  // both Members and Billing (or Usage) in the same session shares one cache
  // entry rather than fetching the same summary twice.
  const query = useQuery({
    queryKey: ['spec-billing', workspaceId],
    queryFn: () => getSpecBilling(workspaceId),
  });
  const data = query.data ?? null;
  const failed = Boolean(query.error);

  // An effect, not a value read during render: `onSummaryChange` sets state
  // on MembersSettingsPage, an ANCESTOR of this component, and writing to
  // another component's state while THIS component renders is exactly the
  // tearing React's render-must-be-pure rule exists to prevent.
  React.useEffect(() => {
    onSummaryChange(data);
  }, [data, onSummaryChange]);

  React.useEffect(() => {
    onLoadFailed(failed);
  }, [failed, onLoadFailed]);

  return null;
}

/**
 * The guard. Calls two hooks of its own -- `useSpecLaunchState()` and a
 * `useEffect` that resets the summary to null while hidden -- and decides
 * whether to ALSO mount `SpecSeatColumnReader`, which owns the one
 * `useQuery` read behind the Tier column. Both of this component's own
 * hooks run unconditionally on every render, so the hook count HERE is
 * invariant across a mid-session launch flip; what changes is only whether
 * `SpecSeatColumnReader` -- a separate component instance, not a skipped
 * hook call -- gets mounted. Mirrors `SpecUsageSection` in
 * usage-settings-page.tsx.
 *
 * The `if (!visible || !workspaceId) return null;` guard sits BELOW this
 * component's own reset effect, not above it, on purpose: that effect has to
 * run on the very render that returns null, or the reset it performs would
 * never fire. "Above every hook the GUARDED BODY owns" still holds -- the
 * guarded body is `SpecSeatColumnReader`'s `useQuery`, and the guard is
 * above that.
 *
 * Unlike that gate (and `SpecBillingCard`'s), hiding the column must NOT hide
 * the rest of the Members page: Members is live for every workspace, with or
 * without Spec. So a hidden or not-yet-loaded workspace reports `null` up
 * rather than unmounting anything the page itself renders.
 */
function SpecSeatColumnGate({
  workspaceId,
  onSummaryChange,
  onLoadFailed,
}: {
  workspaceId: string | null;
  onSummaryChange: (summary: SpecBillingSummary | null) => void;
  onLoadFailed: (failed: boolean) => void;
}) {
  const launchState = useSpecLaunchState();
  const visible = isSpecSurfaceVisible(launchState);

  // Resets the column off when Spec goes dark (or the workspace changes)
  // mid-session -- the one direction `SpecSeatColumnReader` cannot report
  // itself, because once it stops being mounted it never runs again to say
  // so.
  React.useEffect(() => {
    if (!visible || !workspaceId) {
      onSummaryChange(null);
      // Hidden is not failed. Clearing this on the way out keeps a stale error
      // from outliving the reader that produced it.
      onLoadFailed(false);
    }
  }, [visible, workspaceId, onSummaryChange, onLoadFailed]);

  if (!visible || !workspaceId) return null;
  return (
    <SpecSeatColumnReader
      workspaceId={workspaceId}
      onSummaryChange={onSummaryChange}
      onLoadFailed={onLoadFailed}
    />
  );
}

export function MembersSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceSlugRef = React.useRef<string | null>(workspaceSlug);
  const membersRequestIdRef = React.useRef(0);
  workspaceSlugRef.current = workspaceSlug;
  const { effectivePlan } = useEntitlements(workspaceSlug);
  const [members, setMembers] = React.useState<WorkspaceMember[]>([]);
  const [canManageMembers, setCanManageMembers] = React.useState(false);
  const [seats, setSeats] = React.useState<{
    used: number;
    limit: number;
    isOver: boolean;
    quantity: number;
    canManage: boolean;
  } | null>(null);
  const [loadingMembers, setLoadingMembers] = React.useState(true);
  const [membersError, setMembersError] = React.useState<string | null>(null);
  const [memberActionError, setMemberActionError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<MemberFilter>('All');
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<MemberRole>('Member');
  const [inviteMessages, setInviteMessages] = React.useState<string[]>([]);
  const [approvedInviteDomains, setApprovedInviteDomains] = React.useState<string[]>([]);
  const [sendingInvites, setSendingInvites] = React.useState(false);
  const [removeDialogMember, setRemoveDialogMember] = React.useState<WorkspaceMember | null>(null);
  const [savingMemberAction, setSavingMemberAction] = React.useState(false);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id ?? null;
  const [manageSeatsOpen, setManageSeatsOpen] = React.useState(false);
  const [specSeatSummary, setSpecSeatSummary] = React.useState<SpecBillingSummary | null>(null);
  // ONE write in flight for the whole Tier column, not one per row. See
  // spec-seat-tier.tsx's top-of-file comment: the assignment route replaces
  // the whole set from whatever a request carries, so two rows writing
  // concurrently can each read the pre-write summary and silently overwrite
  // one another. Lifted here (rather than left as each row's own local
  // `saving` state) so every row can be disabled for the duration of ANY
  // row's write, not just the row that started it. It is a within-TAB guard;
  // the route's `expectedMemberIds` fence is what covers a second tab, and
  // `resyncSpecAssignments` below is how this page adopts its answer.
  const [specSeatWriteInFlight, setSpecSeatWriteInFlight] = React.useState(false);
  /**
   * Adopt the server's current assignment set after a write was refused as
   * stale.
   *
   * A patch rather than a replacement, because a stale refusal carries the
   * assignment set and nothing else. Everything else on the summary -- the
   * purchased quantity, the balance, the period -- is untouched by an
   * assignment write, so keeping it is correct and inventing it would not be.
   * `assignedSeatCount` moves with the set because it IS the size of the set,
   * and it is what the remaining-seats arithmetic on this page reads.
   */
  const resyncSpecAssignments = React.useCallback((assignedMemberIds: readonly string[]) => {
    setSpecSeatSummary((current) =>
      current
        ? {
            ...current,
            assignedMemberIds: [...assignedMemberIds],
            assignedSeatCount: assignedMemberIds.length,
          }
        : current,
    );
  }, []);
  // `read_only` keeps Spec surfaces VISIBLE and refuses every mutation with 409
  // `spec_read_only`, so a live Tier picker there could only produce a rejected
  // write. Same call `spec-billing-card.tsx` makes for its own write controls.
  const specWritesPaused = useSpecLaunchState() !== 'on';
  // Distinct from "no summary": a failed read and a read in flight both leave
  // the summary null, and only one of them owes the member an explanation.
  const [specSeatLoadFailed, setSpecSeatLoadFailed] = React.useState(false);

  // Assignable, workspace-wide: the purchased-or-pending ceiling (whichever
  // the assignment route actually gates on, spec-usage.ts:1051) minus what is
  // already assigned. Zero here disables turning a SPECIFIC row's seat ON; it
  // never disables turning one off, so a fully assigned workspace can still
  // reassign (SpecSeatTierSelect enforces that half itself).
  const specSeatsRemaining = specSeatSummary
    ? Math.max(
        0,
        (specSeatSummary.pendingSeatQuantity ?? specSeatSummary.purchasedSeatQuantity) -
          specSeatSummary.assignedSeatCount,
      )
    : 0;
  // Free has no AI seats. The summary still loads (it carries the purchased
  // credit balance), but a 0 / 0 count and a Tier column would describe a
  // product this workspace no longer has.
  const showAiSeats = specSeatSummary !== null && specSeatSummary.eligibility !== 'free_grant_only';

  const visibleMembers = React.useMemo(
    () => filterMembers(members, query, roleFilter),
    [members, query, roleFilter],
  );
  const activeMemberCount = getActiveMemberCount(members);
  const inviteCount = getInviteCount(members);
  const inviteDomainWarnings = React.useMemo(
    () => getInviteDomainWarnings(inviteEmail, approvedInviteDomains),
    [inviteEmail, approvedInviteDomains],
  );

  const refreshMembers = React.useCallback(async (expectedWorkspaceSlug: string | null) => {
    const requestId = ++membersRequestIdRef.current;
    const isLatestRequest = () =>
      workspaceSlugRef.current === expectedWorkspaceSlug &&
      membersRequestIdRef.current === requestId;

    if (!expectedWorkspaceSlug) {
      setMembers([]);
      setCanManageMembers(false);
      setSeats(null);
      setMembersError('Select a workspace to manage members.');
      setLoadingMembers(false);
      return;
    }

    setLoadingMembers(true);
    try {
      const mapped = mapWorkspaceMembersResponse(await listWorkspaceMembers(expectedWorkspaceSlug));
      if (!isLatestRequest()) return;
      setMembers(mapped.members);
      setCanManageMembers(mapped.canManageMembers);
      setSeats(mapped.seats);
      setMembersError(null);
    } catch (err) {
      if (!isLatestRequest()) return;
      setMembers([]);
      setCanManageMembers(false);
      setSeats(null);
      setMembersError(err instanceof Error ? err.message : 'Unable to load members.');
    } finally {
      if (isLatestRequest()) setLoadingMembers(false);
    }
  }, []);

  React.useEffect(() => {
    let active = true;
    const expectedWorkspaceSlug = workspaceSlug;
    const requestId = ++membersRequestIdRef.current;
    const isLatestRequest = () =>
      active &&
      workspaceSlugRef.current === expectedWorkspaceSlug &&
      membersRequestIdRef.current === requestId;
    setSendingInvites(false);
    setSavingMemberAction(false);
    setInviteOpen(false);
    setManageSeatsOpen(false);
    setInviteEmail('');
    setInviteRole('Member');
    setInviteMessages([]);
    setApprovedInviteDomains([]);
    setMemberActionError(null);
    setRemoveDialogMember(null);
    setSeats(null);

    async function loadMembers() {
      if (!expectedWorkspaceSlug) {
        if (!isLatestRequest()) return;
        setMembers([]);
        setCanManageMembers(false);
        setSeats(null);
        setMembersError('Select a workspace to manage members.');
        setLoadingMembers(false);
        return;
      }

      setLoadingMembers(true);
      try {
        const mapped = mapWorkspaceMembersResponse(
          await listWorkspaceMembers(expectedWorkspaceSlug),
        );
        if (!isLatestRequest()) return;
        setMembers(mapped.members);
        setCanManageMembers(mapped.canManageMembers);
        setSeats(mapped.seats);
        setMembersError(null);
      } catch (err) {
        if (isLatestRequest()) {
          setMembers([]);
          setCanManageMembers(false);
          setSeats(null);
          setMembersError(err instanceof Error ? err.message : 'Unable to load members.');
        }
      } finally {
        if (isLatestRequest()) setLoadingMembers(false);
      }
    }

    async function loadApprovedDomains() {
      if (!expectedWorkspaceSlug) return;

      try {
        const domains = await getWorkspaceSecurityDomains(expectedWorkspaceSlug);
        if (isLatestRequest()) setApprovedInviteDomains(domains);
      } catch {
        if (isLatestRequest()) setApprovedInviteDomains([]);
      }
    }

    void loadMembers();
    void loadApprovedDomains();

    return () => {
      active = false;
    };
  }, [workspaceSlug]);

  const sendInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!workspaceSlug || !canManageMembers) return;
    const expectedWorkspaceSlug = workspaceSlug;

    const emails = inviteEmail
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean);

    if (!emails.length) return;

    setSendingInvites(true);
    setInviteMessages([]);
    setMemberActionError(null);

    try {
      const role = inviteRole === 'Admin' ? 'admin' : 'member';
      const response = await inviteWorkspaceMembers(expectedWorkspaceSlug, {
        emails,
        role,
      });
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      if (isSupabaseConfigured) {
        await refreshMembers(expectedWorkspaceSlug);
        if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      } else {
        setMembers((currentMembers) =>
          mergeInviteResponsePendingMembers(currentMembers, response, inviteRole),
        );
      }

      const messages = getInviteErrorMessages(response);
      setInviteMessages(messages);

      if (messages.length === 0) {
        // inviteRole is MemberRole ('Admin' | 'Member'), the display casing.
        // Hoist the lowercase mapping into a shared `role` used by both the
        // API call and this capture, so the invited role and the reported role
        // cannot diverge.
        capture('member_invited', { count: emails.length, role });
        setInviteEmail('');
        setInviteRole('Member');
        setInviteOpen(false);
      }
    } catch (err) {
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      const code = apiErrorCode(err);
      setInviteMessages([
        code === 'seat_limit_reached'
          ? 'This invite would exceed your plan seat limit. Remove a member or upgrade to add seats.'
          : err instanceof Error
            ? err.message
            : 'Unable to send invitations.',
      ]);
    } finally {
      if (workspaceSlugRef.current === expectedWorkspaceSlug) setSendingInvites(false);
    }
  };

  const removeMember = async () => {
    if (!removeDialogMember || !workspaceSlug || !canManageMembers) return;
    const expectedWorkspaceSlug = workspaceSlug;
    const expectedMemberId = removeDialogMember.id;

    if (removeDialogMember.sourceType !== 'invitation') {
      setMemberActionError('Removing active members is not available yet.');
      setRemoveDialogMember(null);
      return;
    }

    setSavingMemberAction(true);
    setMemberActionError(null);
    try {
      await revokeWorkspaceInvitation(expectedWorkspaceSlug, expectedMemberId);
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      if (isSupabaseConfigured) {
        await refreshMembers(expectedWorkspaceSlug);
        if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      } else {
        setMembers((currentMembers) => removeInvitationMember(currentMembers, expectedMemberId));
      }
      setRemoveDialogMember(null);
    } catch (err) {
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      setMemberActionError(err instanceof Error ? err.message : 'Unable to revoke invitation.');
    } finally {
      if (workspaceSlugRef.current === expectedWorkspaceSlug) setSavingMemberAction(false);
    }
  };

  const resendInvitation = async (member: WorkspaceMember) => {
    if (!workspaceSlug || !canManageMembers || member.sourceType !== 'invitation') return;
    const expectedWorkspaceSlug = workspaceSlug;

    setSavingMemberAction(true);
    setMemberActionError(null);
    try {
      await resendWorkspaceInvitation(expectedWorkspaceSlug, member.id);
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      if (isSupabaseConfigured) {
        await refreshMembers(expectedWorkspaceSlug);
      } else {
        setMemberActionError('Invitation remains pending in local mode.');
      }
    } catch (err) {
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      setMemberActionError(err instanceof Error ? err.message : 'Unable to resend invitation.');
    } finally {
      if (workspaceSlugRef.current === expectedWorkspaceSlug) setSavingMemberAction(false);
    }
  };

  const copyInviteLink = async (member: WorkspaceMember) => {
    if (!workspaceSlug || !canManageMembers || member.sourceType !== 'invitation') return;
    const expectedWorkspaceSlug = workspaceSlug;

    setSavingMemberAction(true);
    setMemberActionError(null);
    try {
      const { inviteUrl } = await resendWorkspaceInvitation(expectedWorkspaceSlug, member.id);
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      if (!inviteUrl) {
        setMemberActionError('Invite links are unavailable in local mode.');
        return;
      }
      await navigator.clipboard.writeText(inviteUrl);
      toast('Invite link copied. The invitation email was also resent.');
      await refreshMembers(expectedWorkspaceSlug);
    } catch (err) {
      if (workspaceSlugRef.current !== expectedWorkspaceSlug) return;
      setMemberActionError(err instanceof Error ? err.message : 'Unable to copy invite link.');
    } finally {
      if (workspaceSlugRef.current === expectedWorkspaceSlug) setSavingMemberAction(false);
    }
  };

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Workspace"
        title="Members"
        description="Invite teammates and manage who can access this workspace."
      />
      <SpecSeatColumnGate
        workspaceId={workspaceId}
        onSummaryChange={setSpecSeatSummary}
        onLoadFailed={setSpecSeatLoadFailed}
      />
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by name or email"
                className="pl-9"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" className="w-28 justify-between">
                  {roleFilter}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-32">
                <DropdownMenuRadioGroup
                  value={roleFilter}
                  onValueChange={(value) => setRoleFilter(value as MemberFilter)}
                >
                  {memberFilters.map((filter) => (
                    <DropdownMenuRadioItem key={filter} value={filter}>
                      {filter}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex gap-2">
            {seats?.canManage ? (
              <Button
                type="button"
                variant="outline"
                disabled={!workspaceId}
                onClick={() => setManageSeatsOpen(true)}
              >
                Manage seats
              </Button>
            ) : null}
            <Button
              type="button"
              onClick={() => {
                setInviteMessages([]);
                setInviteOpen(true);
              }}
              disabled={
                !canManageMembers || loadingMembers || (seats ? seats.used >= seats.limit : false)
              }
            >
              <UserPlus className="h-4 w-4" />
              Invite
            </Button>
          </div>
        </div>
        {seats && seatBannerState(seats.used, seats.limit) === 'at' ? (
          <p className="text-xs text-muted-foreground">
            {seats.canManage
              ? 'You have used all your seats. Add seats to invite more.'
              : 'You have used all your seats. Upgrade to invite more.'}
          </p>
        ) : null}
        {membersError ? <p className="text-sm text-destructive">{membersError}</p> : null}
        {memberActionError ? (
          <p className="text-sm text-muted-foreground">{memberActionError}</p>
        ) : null}
        {!canManageMembers && !loadingMembers ? (
          <p className="text-sm text-muted-foreground">
            You can view members, but your role cannot manage invitations for this workspace.
          </p>
        ) : null}

        {seats && workspaceSlug ? (
          <SeatBanner
            used={seats.used}
            limit={seats.limit}
            effectivePlan={effectivePlan}
            workspaceSlug={workspaceSlug}
            canManageSeats={seats.canManage}
            onManageSeats={() => setManageSeatsOpen(true)}
            manageDisabled={!workspaceId}
          />
        ) : null}

        {seats?.canManage && workspaceId ? (
          <ManageSeatsDialog
            open={manageSeatsOpen}
            onOpenChange={setManageSeatsOpen}
            workspaceId={workspaceId}
            includedSeats={seats.limit - seats.quantity}
            currentQuantity={seats.quantity}
            onCommitted={() => refreshMembers(workspaceSlug)}
          />
        ) : null}

        <SettingsPanel>
          <div className="flex items-center gap-3 border-b bg-muted/60 px-4 py-2 text-sm text-muted-foreground">
            <span>Active</span>
            <span>{activeMemberCount}</span>
            <span className="h-1 w-1 rounded-full bg-muted-foreground/40" aria-hidden />
            <span>Invites</span>
            <span>{inviteCount}</span>
            {seats ? (
              <>
                <span className="h-1 w-1 rounded-full bg-muted-foreground/40" aria-hidden />
                <span
                  className={
                    seatBannerState(seats.used, seats.limit) === 'ok'
                      ? 'text-brand'
                      : 'text-amber-700 dark:text-amber-400'
                  }
                >
                  {seats.used} / {seats.limit} seats
                </span>
              </>
            ) : null}
            {showAiSeats && specSeatSummary ? (
              <>
                <span className="h-1 w-1 rounded-full bg-muted-foreground/40" aria-hidden />
                <span
                  className={
                    specSeatsRemaining > 0 ? 'text-brand' : 'text-amber-700 dark:text-amber-400'
                  }
                >
                  {seatPhrase(
                    specSeatSummary.assignedSeatCount,
                    specSeatSummary.purchasedSeatQuantity,
                  )}{' '}
                  AI seats
                </span>
              </>
            ) : null}
          </div>
          {/*
            The Tier column and every AI-seat control vanish when this read
            fails, because they are all driven by the summary it returns. That
            used to happen in total silence -- a whole column of controls simply
            absent, with nothing to distinguish it from a workspace that has no
            Spec at all.
          */}
          {specSeatLoadFailed ? (
            <p
              className="border-b bg-muted/30 px-4 py-2 text-sm text-muted-foreground"
              data-testid="spec-seat-load-error"
            >
              AI seat details are unavailable right now, so the Tier column is hidden. Reload in a
              few minutes to manage AI seats.
            </p>
          ) : null}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Role</TableHead>
                {showAiSeats ? <TableHead className="w-40">Tier</TableHead> : null}
                <TableHead>Last active</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingMembers ? <TableSkeletonRows columns={showAiSeats ? 7 : 6} rows={3} /> : null}
              {!loadingMembers && visibleMembers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={showAiSeats ? 7 : 6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No members found.
                  </TableCell>
                </TableRow>
              ) : null}
              {!loadingMembers &&
                visibleMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-medium text-foreground">
                          {member.avatarUrl ? (
                            <AuthenticatedImage
                              src={member.avatarUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            getMemberInitials(member)
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {member.email.split('@')[0]}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{member.email}</TableCell>
                    <TableCell>
                      <MemberStatusBadge member={member} />
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.role === 'Admin' ? 'brand' : 'secondary'}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    {showAiSeats && specSeatSummary && workspaceId ? (
                      <TableCell>
                        <SpecSeatTierSelect
                          workspaceId={workspaceId}
                          memberId={member.id}
                          assignedMemberIds={specSeatSummary.assignedMemberIds}
                          assigned={specSeatSummary.assignedMemberIds.includes(member.id)}
                          seatsRemaining={specSeatsRemaining}
                          canManage={canManageMembers && !specWritesPaused}
                          memberActive={member.status === 'Active'}
                          writeInFlight={specSeatWriteInFlight}
                          onWriteInFlightChange={setSpecSeatWriteInFlight}
                          onSummaryChange={setSpecSeatSummary}
                          onAssignmentsChanged={resyncSpecAssignments}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <span className={cn(member.lastActive === 'Online' && 'text-brand')}>
                        {member.lastActive}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={!canManageMembers || savingMemberAction}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Member actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {member.status === 'Active' ? (
                            <>
                              <DropdownMenuItem disabled>Change role unavailable</DropdownMenuItem>
                              <DropdownMenuItem disabled>Update name unavailable</DropdownMenuItem>
                              <DropdownMenuItem disabled>Update email unavailable</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled
                              >
                                <Trash2 className="h-4 w-4" />
                                Remove unavailable
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <>
                              <DropdownMenuItem onClick={() => void resendInvitation(member)}>
                                Resend invitation
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => void copyInviteLink(member)}>
                                Copy invite link
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setRemoveDialogMember(member)}
                              >
                                <Trash2 className="h-4 w-4" />
                                Revoke invitation...
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </SettingsPanel>

        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite to your workspace</DialogTitle>
              <DialogDescription>
                Invite one or more people by email and choose their starting role.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={sendInvite} className="space-y-5">
              {inviteMessages.length > 0 ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
                  {inviteMessages.map((message) => (
                    <p key={message}>{message}</p>
                  ))}
                </div>
              ) : null}
              <div className="grid gap-2">
                <Label htmlFor="member-invite-email">Email</Label>
                <Input
                  id="member-invite-email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="email@company.com, teammate@company.com"
                  disabled={sendingInvites}
                />
                {inviteDomainWarnings.length > 0 ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 text-sm dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-300">
                    {inviteDomainWarnings.map((warning) => (
                      <p key={warning}>{warning}</p>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="grid gap-2">
                <Label>Role</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between"
                      disabled={sendingInvites}
                    >
                      {inviteRole}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-[--radix-dropdown-menu-trigger-width]"
                  >
                    <DropdownMenuRadioGroup
                      value={inviteRole}
                      onValueChange={(value) => setInviteRole(value as MemberRole)}
                    >
                      <DropdownMenuRadioItem value="Member">
                        Member - Standard workspace access
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="Admin">
                        Admin - Full administrative access
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline" disabled={sendingInvites}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={!inviteEmail.trim() || sendingInvites}>
                  {sendingInvites ? 'Sending...' : 'Send invites'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={Boolean(removeDialogMember)}
          onOpenChange={(open) => {
            if (!open) setRemoveDialogMember(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {removeDialogMember?.status === 'Active'
                  ? `Remove ${removeDialogMember.name}?`
                  : `Revoke invitation for ${removeDialogMember?.email ?? 'this person'}?`}
              </DialogTitle>
              <DialogDescription>
                {removeDialogMember?.status === 'Active'
                  ? 'Active member removal is not available yet.'
                  : 'They will no longer be able to accept this workspace invitation.'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                onClick={() => void removeMember()}
                disabled={savingMemberAction}
              >
                {removeDialogMember?.status === 'Active' ? 'Remove member' : 'Revoke invitation'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </section>
    </SettingsShell>
  );
}
